import {type} from 'arktype';
import {ComponentProps} from 'react';
import {RsnDialog} from '~/library/ui-kit';

const BYPASS_TEXT = 'Something went wrong';
const CONTEXT_500 = `We’re sorry that you have to experience some problems. Don't worry, we'll fix it. Please try again later.`;
const BIOMETRY_ATTEMPTS_EXCEEDED = 'Failed to recognize biometrics';
const BIOMETRY_CONTEXT_ATTEMPTS_EXCEEDED = 'Please try again later.';

export const ErrorHandler = type({
    'code?': 'number',
    'title?': 'string',
    'context?': 'string',
    'message?': 'string',
    'type?': 'string',
});

export type ErrorHandler = typeof ErrorHandler.infer;

export interface ErrorHandlerReturnData {
    code?: number;
    title: string;
    context: string;
    link?: string;
    type?: ComponentProps<typeof RsnDialog.StatusIcon>['status'];
}

export function getDefaultErrorText() {
    return getErrorText({});
}

export function getErrorText(err: ErrorHandler): ErrorHandlerReturnData {
    const err500: ErrorHandlerReturnData = {
        title: BYPASS_TEXT,
        context: CONTEXT_500,
        code: 500,
    };
    const biometryAttemptsExceeded: ErrorHandlerReturnData = {
        title: BIOMETRY_ATTEMPTS_EXCEEDED,
        context: BIOMETRY_CONTEXT_ATTEMPTS_EXCEEDED,
        code: 401,
        type: 'alert',
    };

    const {message} = err;
    const {code} = err;
    const messageLowerCase = message?.toLowerCase() || '';

    if (messageLowerCase.includes('please try again in')) {
        return {
            code,
            title: 'You need to wait a bit',
            context: message ?? 'Please try again later.',
            type: 'alert',
        };
    }

    if (messageLowerCase.includes('usernames are not equal'))
        return {
            code,
            title: message || '',
            context: 'You need to log in using your username and password',
            type: 'alert',
        };

    if (err.type === 'biometry') return biometryAttemptsExceeded;

    //Possible errors from API
    /*
    Wrong login or password
    Account is temporary blocked
    Account is permanently blocked
    Account is disabled
    Account is deleted
    Account is refused
    This account is closed. To restore it, contact support
    */

    if (messageLowerCase.includes('temporary blocked'))
        return {title: 'This account is temporary blocked', context: '', code: 400};
    if (messageLowerCase.includes('permanently blocked'))
        return {title: 'This account is permanently blocked', context: '', code: 400};
    if (messageLowerCase.includes('disabled')) return {title: 'This account is disabled', context: '', code: 400};
    if (messageLowerCase.includes('deleted')) return {title: 'This account is deleted', context: '', code: 400};
    if (messageLowerCase.includes('refused')) return {title: 'This account is refused', context: '', code: 400};
    if (messageLowerCase.includes('closed')) return {title: 'This account is closed', context: '', code: 400};

    if (messageLowerCase.includes('wrong login or password from login'))
        return {title: 'Invalid password or phone number', context: '', code: 400};

    if (messageLowerCase.includes('wrong login or password from signup'))
        return {
            title: 'Invalid code',
            context: 'You entered the wrong code. Check the correctness and try again.',
            code: 401,
            type: 'alert',
        };

    if (messageLowerCase.includes('login or password'))
        return {
            title: 'Invalid password',
            context: 'You entered the wrong password. Check the correctness and try again.',
            code: 401,
            type: 'alert',
        };

    /*
     * Reverification (SumSub)
     * https://jira.raison.digital/browse/RAISON-4567
     * code is always 116 for reverification errors
     * link separator is always \n
     */
    if (code === 116) {
        const link = messageLowerCase.split('\n')[1];
        return link
            ? {
                  title: 'Additional checks needed',
                  context: 'Please proceed with additional verification to restore your access.',
                  code: 403,
                  link,
                  type: 'alert',
              }
            : {
                  title: 'Additional checks are pending',
                  context: 'Please wait for the process to complete, we will notify you when it is ready.',
                  code: 403,
                  type: 'progress',
              };
    }

    /*
     * Document expired (SumSub)
     * https://jira.raison.digital/browse/RAISON-4606
     * code is always 117 for document expired errors
     * link separator is always \n
     */
    if (code === 117) {
        const link = messageLowerCase.split('\n')[1];
        return link
            ? {
                  title: 'Document expired',
                  context: 'Please proceed with the ID verification to restore your access.',
                  code: 403,
                  link,
                  type: 'alert',
              }
            : {
                  title: 'Document verification is pending',
                  context: 'Please wait for the process to complete, we will notify you when it is ready.',
                  code: 403,
                  type: 'progress',
              };
    }

    if (message)
        return {
            title: BYPASS_TEXT,
            context: message,
            code: 500,
        };

    return err500;
}
